import client from '../client';
import type { AnalyticsData, AnalyticsTimeSeries, TopProduct, TopCustomer, CategoryBreakdown, DashboardWidgets, TrafficSource, DeviceBreakdown, CountryBreakdown, ConversionFunnel, CustomerBreakdown, TopPage, HourlyVisit } from '../../types/analytics';
import type { ApiResponse } from '../../types/api';

export const analyticsApi = {
  getWidgets: async (storeId: string) => {
    const res = await client.get<ApiResponse<DashboardWidgets>>(
      `/dashboard/${storeId}/widgets`,
    );
    return res.data.data!;
  },

  saveWidgetLayout: async (storeId: string, layout: Array<{ id: string; type: string; size: string; position: number; visible: boolean }>) => {
    const res = await client.post<ApiResponse<{ layout: unknown[] }>>(
      `/dashboard/${storeId}/widgets/layout`,
      { layout },
    );
    return res.data.data!;
  },

  getCountryStats: async (storeId: string, country: string) => {
    const res = await client.get<ApiResponse<{
      stats: { visits: number; orders: number; revenue: number; activeNow: number };
    }>>(
      `/dashboard/${storeId}/analytics/country-stats/${encodeURIComponent(country)}`,
    );
    return res.data.data!;
  },

  getSearchAnalytics: async (storeId: string, type: 'popular' | 'failed' | 'recent' = 'popular', limit = 20, days = 30) => {
    const res = await client.get<ApiResponse<{
      searches: Array<{
        query: string;
        count: number;
        resultCount: number;
        lastSearched: string;
        uniqueVisitors?: number;
        uniqueCustomers?: number;
        uniqueSessions?: number;
      }>;
      stats: {
        totalSearches: number;
        uniqueTerms: number;
        failedSearches: number;
        uniqueVisitors?: number;
        uniqueCustomers?: number;
        uniqueSessions?: number;
        avgSearchesPerSession?: number;
      };
    }>>(
      `/dashboard/${storeId}/analytics/search`,
      { params: { type, limit, days } },
    );
    return res.data.data!;
  },

  getAnalytics: async (storeId: string, period?: string) => {
    const res = await client.get<ApiResponse<{
      stats: AnalyticsData;
      timeSeries: AnalyticsTimeSeries[];
      topProducts: TopProduct[];
      topCustomers: TopCustomer[];
      categoryBreakdown: CategoryBreakdown[];
      paymentMethodBreakdown: Array<{ method: string; count: number; revenue: number }>;
      orderStatusBreakdown: Array<{ status: string; count: number }>;
      traffic?: { pageViews: number; uniqueVisitors: number; bounceRate: number };
      trafficSources?: TrafficSource[];
      deviceBreakdown?: DeviceBreakdown[];
      countryBreakdown?: CountryBreakdown[];
      conversionFunnel?: ConversionFunnel;
      customers?: CustomerBreakdown;
      topPages?: TopPage[];
      activeVisitors?: number;
      hourlyVisits?: HourlyVisit[];
      last24hVisits?: number;
    }>>(
      `/dashboard/${storeId}/analytics`,
      { params: period ? { period } : undefined },
    );
    return res.data.data!;
  },
};
